/**
 * React Query hooks for the team (XC-TEAM-INVITES Phase 1). The Team page, the invite window, the
 * Inbox's invite-request cards and the sidebar's Inbox count read one key, so they never disagree;
 * every write re-reads the team and its Activity before it settles.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  actOnInvite,
  approveRequest,
  changeRole,
  declineRequest,
  fetchActivity,
  fetchTeam,
  fetchUsage,
  sendInvites,
  setActive,
  type InviteAct,
} from '@/services/teamService';
import type { ActivityItem, InviteResult, Team, TeamRole, TeamUsage, UsageRange } from '@/types/team';

export const teamKeys = {
  team: ['team'] as const,
  activity: ['team', 'activity'] as const,
  usage: (days: UsageRange, zone: string) => ['team', 'usage', days, zone] as const,
};

export function useTeam(enabled = true) {
  return useQuery<Team>({
    queryKey: teamKeys.team,
    queryFn: fetchTeam,
    staleTime: 30_000,
    enabled,
  });
}

export function useTeamActivity(enabled = true) {
  return useQuery<ActivityItem[]>({
    queryKey: teamKeys.activity,
    queryFn: () => fetchActivity(),
    staleTime: 30_000,
    enabled,
  });
}

/** The team's use over time (every role). A new range keeps the last chart until it lands. */
export function useTeamUsage(days: UsageRange, zone: string) {
  return useQuery<TeamUsage>({
    queryKey: teamKeys.usage(days, zone),
    queryFn: () => fetchUsage(days, zone),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

function useTeamWrite<TArgs, TResult = void>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation<TResult, unknown, TArgs>({
    mutationFn: fn,
    // ['team'] is the prefix of the Activity key too: one invalidation re-reads both.
    onSettled: () => qc.invalidateQueries({ queryKey: teamKeys.team }),
  });
}

export function useSendInvites() {
  return useTeamWrite<{ emails: string[]; role: TeamRole; note: string | null }, InviteResult>(
    ({ emails, role, note }) => sendInvites(emails, role, note),
  );
}

export function useInviteAct() {
  return useTeamWrite<{ inviteId: string; act: InviteAct }>(({ inviteId, act }) =>
    actOnInvite(inviteId, act),
  );
}

export function useApproveRequest() {
  return useTeamWrite<{ inviteId: string; role?: TeamRole }>(({ inviteId, role }) =>
    approveRequest(inviteId, role),
  );
}

export function useDeclineRequest() {
  return useTeamWrite<{ inviteId: string }>(({ inviteId }) => declineRequest(inviteId));
}

export function useChangeRole() {
  return useTeamWrite<{ userId: string; role: TeamRole }>(({ userId, role }) =>
    changeRole(userId, role),
  );
}

export function useSetActive() {
  return useTeamWrite<{ userId: string; active: boolean }>(({ userId, active }) =>
    setActive(userId, active),
  );
}
